import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { format, parseISO, isWithinInterval } from 'date-fns';
import { FaArrowLeft, FaExchangeAlt } from "react-icons/fa";
import {
  MoreHorizontal,
  Eye,
  Edit2,
  Trash2,
  Filter,
  X,
  RefreshCw,
  Search,
} from "lucide-react";
import { FcUp, FcDown } from "react-icons/fc";

import Pagination from '../helpers/Pagination';
import PageLoader from '../helpers/PageLoader';
import NoDataAvailable from '../helpers/NoDataAvailable';
import DeleteConfirmationOffcanvas from '../helpers/DeleteConfirmationOffcanvas';
import SelectDropdown from '../customStyles/SelectDropdown';
import CustomDatePicker from '../customStyles/CustomDatePicker';
import api from '../common/apiClient';
import SummaryApi from '../common/SummaryApi';

const OpenLeads = () => {
  const { portalName } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showFilters, setShowFilters] = useState(true);
  const [menuIndex, setMenuIndex] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [sortOrder, setSortOrder] = useState("desc");

  // Options fetched from DB for Edit Modal
  // eslint-disable-next-line no-unused-vars
  const [dbStages, setDbStages] = useState([]);
  
  // State to hold fetched RM, BDE, and DM executives for lead assignment
  const [executives, setExecutives] = useState([]);

  // ==========================================
  // VIEW & EDIT MODALS STATE
  // ==========================================
  const [viewModal, setViewModal] = useState({ isOpen: false, lead: null });
  const [editModal, setEditModal] = useState({
    isOpen: false,
    lead: null,
    isSaving: false,
    formData: {
      fullName: '',
      email: '',
      mobile: '',
      industry: '',
      source: '',
      assignedTo: '',
      remarks: ''
    }
  });

  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    selectedData: null,
    error: null,
  });

  // Filter States (Leads List)
  const [filters, setFilters] = useState({
    status: '',
    source: '',
    industry: '',
    fromDate: '',
    toDate: '',
  });

  const [activeFiltersCount, setActiveFiltersCount] = useState(0);

  const [filterOptions, setFilterOptions] = useState({
    sources: [
      { value: 'Healthcare Assessment', label: 'Healthcare Assessment' },
      { value: 'Meta Leads', label: 'Meta Leads' },
      { value: 'Manual', label: 'Manual Input' }
    ],
    statuses: [
      { value: 'OPEN', label: 'Open' },
      { value: 'CONVERTED', label: 'Converted' },
      { value: 'CONTACTED', label: 'Contacted' }
    ],
    industries: []
  });

  // Pagination
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Handle click outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setMenuIndex(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch Stages from DB using SummaryApi reference
  const fetchDbDropdowns = useCallback(async () => {
    try {
      const params = {
        includeInactive: false,
        includeShared: true,
      };
      const stageRes = await api.get(SummaryApi.getAllStages.url, { params }).catch(() => null);
      if (stageRes?.data?.success) {
        setDbStages(stageRes.data.data || []);
      }
    } catch (err) {
      console.error("Dropdown loading failed", err);
    }
  }, []);

  // Fetch executives with RM, BDE, or DM roles dynamically
  const fetchExecutives = useCallback(async () => {
    try {
      const url = SummaryApi.executiveUsers?.url || '/system/executives';
      const response = await api.get(url);
      if (response?.data?.success) {
        setExecutives(response.data.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch executives list:", err);
    }
  }, []);

  // Extract unique industries dynamically for filter options
  const extractFilterOptions = useCallback((leadsData) => {
    const uniqueIndustries = [...new Set(leadsData.map(l => l.industry).filter(Boolean))];
    const uniqueSources = [...new Set(leadsData.map(l => l.source).filter(Boolean))];

    setFilterOptions(prev => ({
      ...prev,
      industries: uniqueIndustries.map(ind => ({ value: ind, label: ind })),
      sources: [
        { value: 'Healthcare Assessment', label: 'Healthcare Assessment' },
        { value: 'Meta Leads', label: 'Meta Leads' },
        ...uniqueSources
          .filter(src => src !== 'Healthcare Assessment' && src !== 'Meta Leads')
          .map(src => ({ value: src, label: src }))
      ]
    }));
  }, []);

  // Fetch Open Leads using SummaryApi.getOpenLeads.url
  const fetchLeads = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(SummaryApi.getOpenLeads.url);
      if (response.data.success) {
        const leadsData = response.data.data || [];
        setLeads(leadsData);
        extractFilterOptions(leadsData);
      }
    } catch (err) {
      setError('Failed to fetch open leads');
      console.error(err);
      toast.error('Failed to fetch open leads');
    } finally {
      setLoading(false);
    }
  }, [extractFilterOptions]);

  useEffect(() => {
    fetchLeads();
    fetchDbDropdowns();
    fetchExecutives(); // Triggers executive lookup on page mount
  }, [fetchLeads, fetchDbDropdowns, fetchExecutives]);

  // Filter application pipeline
  useEffect(() => {
    let result = [...leads];

    result.sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      result = result.filter(lead => 
        (lead.fullName && lead.fullName.toLowerCase().includes(searchLower)) ||
        (lead.email && lead.email.toLowerCase().includes(searchLower)) ||
        (lead.mobile && lead.mobile.includes(searchLower)) ||
        (lead.leadNumber && lead.leadNumber.toLowerCase().includes(searchLower))
      );
    }

    if (filters.fromDate || filters.toDate) {
      result = result.filter(lead => {
        const createdDate = parseISO(lead.createdAt);
        const fromDate = filters.fromDate ? new Date(filters.fromDate + 'T00:00:00') : new Date('1900-01-01');
        const toDate = filters.toDate ? new Date(filters.toDate + 'T23:59:59.999') : new Date('2100-12-31');
        return isWithinInterval(createdDate, { start: fromDate, end: toDate });
      });
    }

    if (filters.status) result = result.filter(l => l.status === filters.status);
    if (filters.source) result = result.filter(l => l.source === filters.source);
    if (filters.industry) result = result.filter(l => l.industry === filters.industry);

    setFilteredLeads(result);
    setCurrentPage(1);
  }, [leads, filters, searchTerm, sortOrder]);

  // Sync URL Params
  useEffect(() => {
    if (!isInitialized) return;
    const params = new URLSearchParams();
    if (searchTerm) params.set("search", searchTerm);
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    navigate(`?${params.toString()}`, { replace: true });
  }, [filters, searchTerm, navigate, isInitialized]);

  useEffect(() => {
    if (isInitialized) return;
    const params = new URLSearchParams(location.search);
    setFilters({
      status: params.get("status") || "",
      source: params.get("source") || "",
      industry: params.get("industry") || "",
      fromDate: params.get("fromDate") || "",
      toDate: params.get("toDate") || "",
    });
    setSearchTerm(params.get("search") || "");
    setIsInitialized(true);
  }, [location.search, isInitialized]);

  // Count active filters
  useEffect(() => {
    let count = 0;
    if (searchTerm.trim()) count++;
    if (filters.fromDate || filters.toDate) count++;
    if (filters.status) count++;
    if (filters.source) count++;
    if (filters.industry) count++;
    setActiveFiltersCount(count);
  }, [filters, searchTerm]);

  const toggleSort = () => setSortOrder(prev => (prev === "asc" ? "desc" : "asc"));

  const clearAllFilters = () => {
    setFilters({ status: '', source: '', industry: '', fromDate: '', toDate: '' });
    setSearchTerm('');
  };

  const clearFilter = (key) => setFilters(prev => ({ ...prev, [key]: '' }));

  // Handle Lead Deletion using SummaryApi.deleteOpenLead
  const handleDelete = async (id) => {
    try {
      const response = await api.delete(SummaryApi.deleteOpenLead.url(id));
      if (response.data.success) {
        setLeads(prev => prev.filter(l => l._id !== id));
        toast.success('Lead deleted successfully');
        setDeleteConfirmation({ isOpen: false, selectedData: null, error: null });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error deleting lead');
    }
  };

  // Status Badge Colors
  const getStatusColor = (status) => {
    const colors = {
      'OPEN': 'bg-blue-100 text-blue-800',
      'CONVERTED': 'bg-green-100 text-green-800',
      'CONTACTED': 'bg-yellow-100 text-yellow-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getSourceBadgeColor = (source) => {
    if (source === 'Meta Leads') return 'bg-indigo-100 text-indigo-800';
    if (source === 'Healthcare Assessment') return 'bg-teal-100 text-teal-800';
    return 'bg-purple-100 text-purple-800';
  };

  // NEW REDIRECT TO NEW PAGE FOR CONVERT ACTION
  const handleConvertLeadRedirect = (leadId) => {
    navigate(`/${portalName}/sales/leads/convert/${leadId}`);
    setMenuIndex(null);
  };

  // ==========================================
  // VIEW DETAILS MODAL ACTIONS
  // ==========================================
  const openViewModal = (lead) => {
    setViewModal({ isOpen: true, lead });
    setMenuIndex(null);
  };

  // ==========================================
  // EDIT / ASSIGN MODAL ACTIONS
  // ==========================================
  const openEditModal = (lead) => {
    setEditModal({
      isOpen: true,
      lead,
      isSaving: false,
      formData: {
        fullName: lead.fullName || '',
        email: lead.email || '',
        mobile: lead.mobile || '',
        industry: lead.industry || '',
        source: lead.source || '',
        assignedTo: lead.assignedTo || '',
        remarks: lead.remarks || ''
      }
    });
    setMenuIndex(null);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditModal(prev => ({ ...prev, isSaving: true }));

    try {
      // Bulletproof URL Resolution
      const url = typeof SummaryApi.updateOpenLead?.url === 'function'
        ? SummaryApi.updateOpenLead.url(editModal.lead._id)
        : `${SummaryApi.updateOpenLead?.url || '/api/open-leads/update'}/${editModal.lead._id}`;

      const response = await api.put(url, editModal.formData);
      
      if (response.data.success) {
        setLeads(prev => prev.map(l => l._id === editModal.lead._id ? response.data.data : l));
        toast.success('Lead updated successfully!');
        setEditModal({ isOpen: false, lead: null, isSaving: false, formData: {} });
      }
    } catch (err) {
      console.error("Update Lead Error Log:", err);
      // Detailed error log extraction to toast
      const errorMsg = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to update lead';
      toast.error(errorMsg);
    } finally {
      setEditModal(prev => ({ ...prev, isSaving: false }));
    }
  };

  const totalItems = filteredLeads.length;
  const paginatedData = itemsPerPage === "All"
    ? filteredLeads
    : filteredLeads.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  let serialNumber = 1;

  // Format executive dropdown list with dual value compatibility (id and value)
  const executiveOptions = executives.map(exe => {
    const displayName = exe.fullName || `${exe.firstName || ''} ${exe.lastName || ''}`.trim() || exe.userId || 'Unknown';
    const roleName = exe.role || exe.customRoleId?.roleName || 'Executive';
    return {
      id: displayName,
      value: displayName,
      label: `${displayName} (${roleName})`
    };
  });

  return (
    <div className="w-full p-4">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <button onClick={() => navigate(-1)} className="mr-2 hover:bg-gray-300 rounded-full p-1">
            <FaArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl md:text-2xl font-bold">Open Leads</h1>
            {activeFiltersCount > 0 && (
              <span className="ml-2 px-2 py-1 text-xs bg-red-100 text-red-700 rounded-full">
                {activeFiltersCount} filters
              </span>
            )}
          </div>
        </div>
        
        {/* Search */}
        <div className="relative w-full md:w-64">
          <input
            type="text"
            placeholder="Search name, phone, lead no..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
          />
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          {searchTerm && (
            <button onClick={() => setSearchTerm("")} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2 rounded-lg transition-colors ${showFilters ? "bg-red-100 text-red-700" : "bg-gray-100 hover:bg-gray-200"}`}
          >
            <Filter className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Filter Section */}
      {showFilters && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
              <CustomDatePicker value={filters.fromDate} onChange={(date) => setFilters(prev => ({ ...prev, fromDate: date }))} placeholder="Select date" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
              <CustomDatePicker value={filters.toDate} onChange={(date) => setFilters(prev => ({ ...prev, toDate: date }))} placeholder="Select date" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
              <SelectDropdown options={filterOptions.sources} value={filters.source} onChange={(val) => setFilters(prev => ({ ...prev, source: val }))} placeholder="All Sources" valueKey="value" labelKey="label" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <SelectDropdown options={filterOptions.statuses} value={filters.status} onChange={(val) => setFilters(prev => ({ ...prev, status: val }))} placeholder="All Statuses" valueKey="value" labelKey="label" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
              <SelectDropdown options={filterOptions.industries} value={filters.industry} onChange={(val) => setFilters(prev => ({ ...prev, industry: val }))} placeholder="All Industries" valueKey="value" labelKey="label" />
            </div>
          </div>

          {/* Active Filtering badging */}
          {activeFiltersCount > 0 && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-sm text-gray-600">Active filters:</span>
              {filters.source && <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm">Source: {filters.source} <button onClick={() => clearFilter("source")}><X className="w-3 h-3 inline ml-1" /></button></span>}
              {filters.status && <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm">Status: {filters.status} <button onClick={() => clearFilter("status")}><X className="w-3 h-3 inline ml-1" /></button></span>}
              {filters.industry && <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm">Industry: {filters.industry} <button onClick={() => clearFilter("industry")}><X className="w-3 h-3 inline ml-1" /></button></span>}
              <button onClick={clearAllFilters} className="ml-auto text-sm text-red-600 hover:text-red-700 flex items-center gap-1">
                <RefreshCw className="w-3 h-3" /> Clear All
              </button>
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-6">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Main Grid / Table */}
      {loading ? (
        <PageLoader />
      ) : paginatedData.length === 0 ? (
        <NoDataAvailable message="No matching open leads found" actionLabel="Create Manual Lead" onAction={() => navigate(`/${portalName}/sales/leads/create-manual`)} />
      ) : (
        <div className="overflow-x-auto rounded-md">
          <table className="min-w-full bg-white border-separate border-spacing-y-2 px-2">
            <thead className="text-sm text-gray-600">
              <tr className="text-center rounded-lg shadow-sm bg-red-100">
                <th className="py-4 px-4 rounded-l-lg">S.No</th>
                <th className="py-4 px-4">Lead ID</th>
                <th className="py-4 px-4">Lead Name</th>
                <th className="py-4 px-4">Contact Detail</th>
                <th className="py-4 px-4">Source</th>
                <th className="py-4 px-4">Score / Perf.</th>
                <th className="py-4 px-4">Industry</th>
                <th className="py-4 px-4">Assigned To</th>
                <th className="py-4 px-4">Status</th>
                <th className="py-4 px-4 cursor-pointer" onClick={toggleSort}>
                  <div className="flex items-center justify-center">
                    Created Date
                    {sortOrder === "asc" ? <FcUp className="w-4 h-4 ml-1" /> : <FcDown className="w-4 h-4 ml-1" />}
                  </div>
                </th>
                <th className="py-4 px-4 rounded-r-lg">Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((lead, index) => (
                <tr key={lead._id} className="bg-gray-50 hover:bg-gray-100 text-sm rounded-lg shadow-sm text-center">
                  <td className="py-3 px-4 font-medium text-gray-700 rounded-l-lg">
                    {itemsPerPage !== "All" ? (currentPage * itemsPerPage) - itemsPerPage + serialNumber++ : serialNumber++}
                  </td>
                  <td className="py-3 px-4 font-semibold text-blue-600">
                    <button onClick={() => openViewModal(lead)} className="hover:underline">
                      {lead.leadNumber}
                    </button>
                  </td>
                  <td className="py-3 px-4 font-semibold text-gray-800">
                    {lead.fullName}
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-xs text-gray-600">{lead.mobile}</div>
                    <div className="text-xs text-gray-400">{lead.email || '-'}</div>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getSourceBadgeColor(lead.source)}`}>
                      {lead.source}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    {lead.source === 'Healthcare Assessment' ? (
                      <div className="font-semibold text-green-700">
                        {lead.score}/{lead.totalQuestions} ({lead.percentage}%)
                      </div>
                    ) : (
                      <span className="text-gray-400 text-xs">N/A</span>
                    )}
                  </td>
                  <td className="py-3 px-4">{lead.industry || 'General'}</td>
                  <td className="py-3 px-4 font-medium text-gray-600">{lead.assignedTo || 'Unassigned'}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${getStatusColor(lead.status)}`}>
                      {lead.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-gray-500 whitespace-nowrap">
                    {lead.createdAt ? format(parseISO(lead.createdAt), "dd MMM yyyy") : '-'}
                  </td>
                  <td className="py-3 px-4 rounded-r-lg">
                    <div className="flex items-center justify-center">
                      <div className="relative inline-block text-left">
                        <button
                          onClick={(e) => {
                            const buttonRect = e.currentTarget.getBoundingClientRect();
                            setMenuIndex(menuIndex?.index === index ? null : {
                              index,
                              position: {
                                top: buttonRect.bottom + window.scrollY,
                                left: buttonRect.right - 144,
                                direction: window.innerHeight - buttonRect.bottom > 150 ? "below" : "above"
                              }
                            });
                          }}
                          className="hover:bg-gray-200 rounded-full p-1"
                        >
                          <MoreHorizontal size={20} />
                        </button>

                        {menuIndex?.index === index && (
                          <div
                            ref={dropdownRef}
                            className="fixed z-50 w-36 bg-white shadow-lg rounded-md border border-gray-200"
                            style={{
                              top: menuIndex.position.direction === "below" ? `${menuIndex.position.top}px` : `${menuIndex.position.top - 120}px`,
                              left: `${menuIndex.position.left}px`,
                            }}
                          >
                            <ul className="text-sm divide-y divide-gray-100">
                              <li
                                className="px-4 py-2 flex items-center gap-2 hover:bg-gray-50 cursor-pointer text-gray-700"
                                onClick={() => openViewModal(lead)}
                              >
                                <Eye size={15} /> View
                              </li>
                              {lead.status !== 'CONVERTED' && (
                                <>
                                  <li
                                    className="px-4 py-2 flex items-center gap-2 hover:bg-gray-50 cursor-pointer text-green-700 font-semibold"
                                    onClick={() => handleConvertLeadRedirect(lead._id)}
                                  >
                                    <FaExchangeAlt size={13} /> Convert Visit
                                  </li>
                                  <li
                                    className="px-4 py-2 flex items-center gap-2 hover:bg-gray-50 cursor-pointer text-gray-700"
                                    onClick={() => openEditModal(lead)}
                                  >
                                    <Edit2 size={15} /> Edit / Assign
                                  </li>
                                </>
                              )}
                              <li
                                className="px-4 py-2 flex items-center gap-2 hover:bg-gray-50 cursor-pointer text-red-600"
                                onClick={() => {
                                  setDeleteConfirmation({ isOpen: true, selectedData: lead, error: null });
                                  setMenuIndex(null);
                                }}
                              >
                                <Trash2 size={15} /> Delete
                              </li>
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {paginatedData.length > 0 && (
        <Pagination
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onPageChange={(page) => setCurrentPage(page)}
          onItemsPerPageChange={(value) => {
            setItemsPerPage(value);
            setCurrentPage(1);
          }}
        />
      )}

      {/* ==========================================
          2. VIEW ONE (DETAILS) MODAL
         ========================================== */}
      {viewModal.isOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-end">
          <div className="bg-white h-full w-full max-w-lg shadow-xl p-6 relative overflow-y-auto">
            <button
              onClick={() => setViewModal({ isOpen: false, lead: null })}
              className="absolute right-4 top-4 hover:bg-gray-100 rounded-full p-1"
            >
              <X className="w-6 h-6 text-gray-500" />
            </button>

            <h2 className="text-xl font-bold mb-6 text-gray-800">Lead Details</h2>

            <div className="space-y-6 divide-y divide-gray-100">
              <div className="pb-4">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Primary Info</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-xs text-gray-400 block">Lead ID</span>
                    <span className="text-sm font-semibold text-blue-600">{viewModal.lead?.leadNumber}</span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400 block">Status</span>
                    <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${getStatusColor(viewModal.lead?.status)}`}>
                      {viewModal.lead?.status}
                    </span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-xs text-gray-400 block">Lead Name</span>
                    <span className="text-sm font-medium text-gray-900">{viewModal.lead?.fullName}</span>
                  </div>
                </div>
              </div>

              <div className="py-4">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Contact Details</h3>
                <div className="space-y-2">
                  <div>
                    <span className="text-xs text-gray-400 block">Mobile Number</span>
                    <span className="text-sm text-gray-800 font-medium">{viewModal.lead?.mobile}</span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400 block">Email Address</span>
                    <span className="text-sm text-gray-800 font-medium">{viewModal.lead?.email || '-'}</span>
                  </div>
                </div>
              </div>

              <div className="py-4">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Meta / Source Info</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-xs text-gray-400 block">Source</span>
                    <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${getSourceBadgeColor(viewModal.lead?.source)}`}>
                      {viewModal.lead?.source}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400 block">Industry</span>
                    <span className="text-sm text-gray-800 font-medium">{viewModal.lead?.industry || 'General'}</span>
                  </div>
                  {viewModal.lead?.source === 'Healthcare Assessment' && (
                    <>
                      <div>
                        <span className="text-xs text-gray-400 block">Assessment Score</span>
                        <span className="text-sm font-semibold text-green-700">
                          {viewModal.lead?.score} / {viewModal.lead?.totalQuestions}
                        </span>
                      </div>
                      <div>
                        <span className="text-xs text-gray-400 block">Percentage Match</span>
                        <span className="text-sm font-semibold text-green-700">{viewModal.lead?.percentage}%</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="py-4">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Assignment & Audits</h3>
                <div className="space-y-3">
                  <div>
                    <span className="text-xs text-gray-400 block">Assigned Executive</span>
                    <span className="text-sm text-gray-800 font-medium">{viewModal.lead?.assignedTo || 'Unassigned'}</span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400 block">Created On</span>
                    <span className="text-sm text-gray-600">
                      {viewModal.lead?.createdAt ? format(parseISO(viewModal.lead.createdAt), "dd MMM yyyy, hh:mm a") : '-'}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400 block">Remarks</span>
                    <span className="text-sm text-gray-700 block bg-gray-50 p-2 rounded-md border border-gray-100 min-h-[50px] whitespace-pre-wrap">
                      {viewModal.lead?.remarks || 'No remarks added'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="absolute bottom-6 left-6 right-6 flex gap-3">
              {viewModal.lead?.status !== 'CONVERTED' && (
                <button
                  onClick={() => {
                    handleConvertLeadRedirect(viewModal.lead._id);
                  }}
                  className="flex-1 bg-red-500 text-white font-medium py-2.5 rounded-lg text-sm hover:bg-red-600 flex items-center justify-center gap-2"
                >
                  <FaExchangeAlt /> Convert to Visit
                </button>
              )}
              <button
                onClick={() => setViewModal({ isOpen: false, lead: null })}
                className="flex-1 border border-gray-300 text-gray-700 font-medium py-2.5 rounded-lg text-sm hover:bg-gray-50"
              >
                Close Details
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==========================================
          3. EDIT / ASSIGN LEAD MODAL (FIXED OVERFLOW & CLIPPING)
         ========================================== */}
      {editModal.isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
          {/* 
            FIX: Removed 'max-h-[90vh] overflow-y-auto' from inner modal content div 
            and set 'overflow-visible' to allow select dropdown lists to hover completely 
            outside without getting clipped.
          */}
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 relative overflow-visible">
            <button
              onClick={() => setEditModal({ isOpen: false, lead: null, isSaving: false, formData: {} })}
              className="absolute right-4 top-4 hover:bg-gray-100 rounded-full p-1"
            >
              <X className="w-6 h-6 text-gray-500" />
            </button>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2 text-gray-800">
              <Edit2 className="w-5 h-5 text-red-500" /> Edit / Assign Lead Info
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Updating lead: <strong className="text-gray-800">{editModal.lead?.leadNumber}</strong>
            </p>

            <form onSubmit={handleEditSubmit} className="space-y-4 overflow-visible">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                  <input
                    type="text"
                    required
                    value={editModal.formData.fullName}
                    onChange={(e) => setEditModal(prev => ({
                      ...prev,
                      formData: { ...prev.formData, fullName: e.target.value }
                    }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-red-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number *</label>
                  <input
                    type="text"
                    required
                    value={editModal.formData.mobile}
                    onChange={(e) => setEditModal(prev => ({
                      ...prev,
                      formData: { ...prev.formData, mobile: e.target.value }
                    }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-red-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                  <input
                    type="email"
                    value={editModal.formData.email}
                    onChange={(e) => setEditModal(prev => ({
                      ...prev,
                      formData: { ...prev.formData, email: e.target.value }
                    }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-red-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
                  <input
                    type="text"
                    value={editModal.formData.industry}
                    onChange={(e) => setEditModal(prev => ({
                      ...prev,
                      formData: { ...prev.formData, industry: e.target.value }
                    }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-red-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 overflow-visible">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                  <SelectDropdown
                    options={filterOptions.sources}
                    value={editModal.formData.source}
                    onChange={(val) => setEditModal(prev => ({
                      ...prev,
                      formData: { ...prev.formData, source: val }
                    }))}
                    placeholder="Select Source"
                    valueKey="value"
                    labelKey="label"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assign Executive</label>
                  <SelectDropdown
                    options={executiveOptions}
                    value={editModal.formData.assignedTo}
                    onChange={(val) => setEditModal(prev => ({
                      ...prev,
                      formData: { ...prev.formData, assignedTo: val }
                    }))}
                    zIndex={66}
                    placeholder="Select Executive"
                    valueKey="id"
                    labelKey="label"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <textarea
                  rows="3"
                  value={editModal.formData.remarks}
                  onChange={(e) => setEditModal(prev => ({
                    ...prev,
                    formData: { ...prev.formData, remarks: e.target.value }
                  }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-red-500 resize-none"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setEditModal({ isOpen: false, lead: null, isSaving: false, formData: {} })}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editModal.isSaving}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-md disabled:bg-gray-300"
                >
                  {editModal.isSaving ? 'Saving...' : 'Update Lead Details'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <DeleteConfirmationOffcanvas
        isOpen={deleteConfirmation.isOpen}
        onClose={() => setDeleteConfirmation({ isOpen: false, selectedData: null, error: null })}
        onConfirm={() => handleDelete(deleteConfirmation.selectedData?._id)}
        title="Delete Open Lead"
        itemName={`${deleteConfirmation.selectedData?.fullName} (${deleteConfirmation.selectedData?.leadNumber})`}
        error={deleteConfirmation.error}
      />
    </div>
  );
};

export default OpenLeads;